import { Dispatch, SetStateAction, useState } from 'react';
import { PATHS } from '@client/routes';
import {
  Actions,
  Async,
  F1,
  TMap,
  Typed,
  TypedValue,
} from '../../helpers/types';
import {
  UseCollectionsModelParams,
  useCollectionsModel,
  CollectionsDispatcher,
} from './collections.model';
import {
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
} from './collections.domain';
import { Option } from '@client/new/helpers/types';
import {
  cellIconText,
  CellProps,
  cellMenu,
  CellMenuProps,
} from '../../components/TableShared/Cell';
import { cellHead } from '../../components/TableShared/CellHead';
import {
  Row,
  StandardTableProps,
} from '../../components/StandardTable/StandardTable';
import { getSalesPageUrl } from '../../helpers/landingPages';
import _ from 'lodash';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { DragTableProps } from '@client/new/components/DragTable/DragTable';
import { useTabHash } from '@client/new/hooks/useTabHash';
import {
  getCurrentPath,
  toProgramCheckoutUrl,
  toProgramLandingPageUrl,
} from '@client/helpers/routes';
import {
  SubscriptionEmptyStateOptionsByMode,
  toSubscriptionEmptyStateOptionsByMode,
} from './mappers';
import { CollectionTag } from './components/collectionTag';
import { ProgramLinks } from './components/programLinks';
import { extendDispatch } from '@client/new/helpers/types-utils';
import { faFlagSwallowtail } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFlagSwallowtail as faFlagSwallowtailLight } from '@fortawesome/pro-light-svg-icons';
import { ColorsVars } from '@design-system/vars.css';

export type ListType = LiteCollectionType | 'all';

export type ViewMode =
  | { type: 'empty' }
  | { type: 'drafts-only'; value: SmartTableProps<number> }
  | { type: 'standard'; value: SmartTableProps<number> };

export type CollectionsViewModel = {
  mode: ViewMode;
  listOptions: Array<Option<ListType>>;
  selectedList: ListType;
  dispatch: CollectionViewDispatcher;
  influencerId: number;
  createLink: string;
  emptyState: {
    otp: {
      isGuideOpen: boolean;
      closeGuide: () => void;
      openGuide: () => void;
      name: string;
      guideLink: string;
    };
    subscription: {
      name: string;
      guideLink: string;
      options: SubscriptionEmptyStateOptionsByMode;
    };
  };
  showCreate: boolean;
  selectionModal: {
    open: boolean;
    close: () => void;
  };
  subscription: {
    ppc: {
      requested: boolean;
      enabled: boolean;
      showTag: boolean;
      price: {
        monthly?: number;
        annual?: number;
      };
      priceModal: {
        close: () => void;
        open: () => void;
        isOpen: boolean;
      };
    };
  };
  requestingExclusive: {
    isOpen: boolean;
    onClose: () => void;
    onContinue: () => void;
  };
  linksModal: {
    isOpen: boolean;
    onClose: () => void;
    data?: {
      programName: string;
      links: Array<{ title: string; url: string }>;
    };
  };
  archiveModal: {
    isOpen: boolean;
    close: () => void;
    archive: () => void;
  };
  deleteModal: {
    isOpen: boolean;
    close: () => void;
    onDelete: () => void;
  };
};

export const listTitles: TMap<ListType, string> = {
  all: 'All',
  archived: 'Archived',
  draft: 'Drafts',
  published: 'Published',
};

const optionTypes: ListType[] = ['all', 'draft', 'published', 'archived'];

export const toListOptions = (): CollectionsViewModel['listOptions'] => {
  return optionTypes.map((value) => ({ value, label: listTitles[value] }));
};

type CollectionsType = 'program' | 'category';

export type CollectionsViewActionsMap = {
  selectList: ListType;
  create: { type: CollectionsType };
  edit: number;
  archiveProgram: number;
  deleteProgram: number;
  publishProgram: number;
  reorder: Array<number>;
  openGuide: null;
  closeGuide: null;
  selectPPC: null;
  selectSub: null;
  learnPPC: null;
  learnSub: null;
  closeSelectionModal: null;
  openPriceModal: null;
  closeModal: null;
  closeRequestingExclusiveModal: null;
  requestConvertToPpc: null;
  linksModal: LiteCollection;
  archiveModal: number;
  deleteModal: number;
};

export type CollectionsViewActions = Actions<CollectionsViewActionsMap>;

export type CollectionsViewState = Pick<CollectionsViewModel, 'selectedList'>;

export type CollectionViewDispatcher = F1<CollectionsViewActions>;
export type Model = CollectionsModel & { dispatch: CollectionsDispatcher };
const getDispatcher =
  (
    asyncModel: Async<Model>,
    setTabHash: (v: ListType) => void,
    setViewState: Dispatch<SetStateAction<ViewState>>,
  ): CollectionViewDispatcher =>
  (action) => {
    if (asyncModel.type !== 'fetched') return;
    const model = asyncModel.value;

    switch (action.type) {
      case 'selectList':
        return setTabHash(action.payload);
      case 'archiveProgram':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'none' },
        }));
        return model.dispatch(action);
      case 'publishProgram':
        return model.dispatch(action);
      case 'reorder':
        return model.dispatch(action);
      case 'openGuide':
        setViewState((prev) => ({ ...prev, openModal: { type: 'openGuide' } }));
        return null;
      case 'closeGuide':
        setViewState((prev) => ({ ...prev, openModal: { type: 'none' } }));
        return null;
      case 'selectPPC':
        model.ppc.requested
          ? setViewState((prev) => ({
              ...prev,
              openModal: { type: 'openSelectionModal' },
            }))
          : setViewState((prev) => ({
              ...prev,
              openModal: { type: 'requestingExclusiveModal' },
            }));
        return null;
      case 'selectSub':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'openSelectionModal' },
        }));
        return null;
      case 'closeSelectionModal':
        setViewState((prev) => ({ ...prev, openModal: { type: 'none' } }));
        return null;
      case 'openPriceModal':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'openPriceModal' },
        }));
        return null;
      case 'closeModal':
        setViewState((prev) => ({ ...prev, openModal: { type: 'none' } }));
        return null;
      case 'requestConvertToPpc':
        model.dispatch({ type: 'requestConvertToPpc' });
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'openSelectionModal' },
        }));
        return null;
      case 'linksModal':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'linksModal', value: action.payload },
        }));
        return null;

      case 'archiveModal':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'archiveModal', value: action.payload },
        }));
        return null;
      case 'deleteModal':
        setViewState((prev) => ({
          ...prev,
          openModal: { type: 'deleteModal', value: action.payload },
        }));
        return null;
      case 'deleteProgram':
        return model.dispatch(action);
    }
  };

export const tableSchema: StandardTableProps<number>['schema'] = [
  { label: 'program name', width: 'stretchable' },
  { label: 'status', width: 150 },
  { label: 'rating', width: 100, justify: 'center' },
  { label: 'begins', width: 100, justify: 'center' },
  { label: 'completions', width: 115, justify: 'center' },
  { label: 'comments', width: 100, justify: 'center' },
  { label: 'links', width: 150, justify: 'center' },
  { label: 'actions', width: 110, justify: 'right' },
];

export const getMenu = ({
  programId,
  type,
  accessType,
  dispatch,
  influencerId,
}: {
  programId: number;
  type: LiteCollectionType;
  accessType: LiteCollection['accessType'];
  dispatch: CollectionViewDispatcher;
  influencerId: number;
}): CellMenuProps['value']['items'] => {
  const isOtp = accessType === 'exclusive';

  if (type === 'draft') {
    return [
      {
        text: 'Edit',
        to: PATHS.CATEGORY_EDIT(programId, influencerId),
      },
      {
        text: 'Delete',
        onClick: () => dispatch({ type: 'deleteModal', payload: programId }),
      },
    ];
  }
  if (type === 'archived') {
    return [
      {
        text: isOtp ? 'Preview' : 'Edit',
        to: PATHS.CATEGORY_EDIT(programId, influencerId),
      },
      ...(!isOtp
        ? [
            {
              text: 'Publish',
              onClick: () =>
                dispatch({ type: 'publishProgram', payload: programId }),
            },
          ]
        : []),
    ];
  }
  return [
    {
      text: 'Edit',
      to: PATHS.CATEGORY_EDIT(programId, influencerId),
    },
    {
      text: 'Archive',
      onClick: () => dispatch({ type: 'archiveModal', payload: programId }),
    },
  ];
};

const toLandingPageLink = (
  col: Pick<LiteCollection, 'type' | 'customURL' | 'programId' | 'title'>,
  influencerId: number,
): {
  to: string;
  label: string;
  external?: boolean;
} => {
  return col.type === 'draft'
    ? {
        label: 'Customize Page',
        to: PATHS.PROGRAM_EDIT(col.programId, influencerId, 'sales-page'),
      }
    : {
        label: 'Preview Page',
        to: getSalesPageUrl({
          customURL: col.customURL,
          id: col.programId,
          name: col.title,
        }),
        external: true,
      };
};

export type ToCellsParams = {
  influencerId: number;
  col: LiteCollection;
  dispatch: CollectionViewDispatcher;
  isCreatorLive: boolean;
};

const getChallengeProps = (
  col: LiteCollection,
):
  | { icon: IconProp; iconTooltip: string; iconColor: ColorsVars }
  | undefined => {
  if (!col.hasChallenges) return undefined;
  if (col.hasOngoingChallenges)
    return {
      icon: faFlagSwallowtail,
      iconTooltip: 'This program has ongoing challenge',
      iconColor: 'G-100',
    };
  return {
    icon: faFlagSwallowtailLight,
    iconTooltip: 'This program have past or future challenges',
    iconColor: 'W-100',
  };
};

export const toCells = ({
  col,
  dispatch,
  influencerId,
  isCreatorLive,
}: ToCellsParams): Row['cells'] => {
  const normalize = (v: number) => (col.type === 'draft' ? '-' : `${v}`);

  return [
    cellHead({
      title: col.title,
      link: toLandingPageLink(col, influencerId),
      url: col.thumbnailUrl,
      ...getChallengeProps(col),
    }),
    { type: 'Custom', value: CollectionTag({ type: col.type }) },
    cellIconText(normalize(col.rating), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.begins), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.completions), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    cellIconText(normalize(col.comments), undefined, {
      classNameIcon: 'text-gray-5',
    }),
    {
      type: 'Custom',
      value: ProgramLinks({
        isPublished: col.type === 'published',
        onClick: () => dispatch({ type: 'linksModal', payload: col }),
        isCreatorLive,
      }),
    },
    cellMenu({
      items: getMenu({
        programId: col.programId,
        type: col.type,
        accessType: col.accessType,
        dispatch,
        influencerId,
      }),
    }),
  ];
};

export type ToTablePropsParams = {
  selectedList: CollectionsViewState['selectedList'];
  model: CollectionsModel;
  dispatch: CollectionViewDispatcher;
  isCreatorLive: boolean;
};

export type ToViewModeParams = Omit<ToTablePropsParams, 'isAdmin'>;

export const toCollectionsMode = ({
  model,
  dispatch,
  selectedList,
  isCreatorLive,
}: ToViewModeParams): ViewMode => {
  const isEmpty = _.valuesIn(model.collections).every((i) => _.isEmpty(i));
  if (isEmpty) return { type: 'empty' };
  const value = toSmartTableProps({
    selectedList: selectedList,
    model,
    dispatch,
    isCreatorLive,
  });

  const hasDraftsOnly =
    !_.isEmpty(model.collections.draft) &&
    _.isEmpty(model.collections.published) &&
    _.isEmpty(model.collections.archived);
  if (hasDraftsOnly) return { type: 'drafts-only', value };
  return { type: 'standard', value };
};

export const toTableProps = ({
  selectedList,
  model,
  dispatch,
  isCreatorLive,
}: ToTablePropsParams): StandardTableProps<number> => {
  const data =
    selectedList === 'all'
      ? _.sortBy(
          [
            ...model.collections.published,
            ...model.collections.draft,
            ...model.collections.archived,
          ],
          ['index'],
        )
      : _.sortBy(model.collections[selectedList], ['index']);

  const rows = data.map((col) => {
    const collectionState = model.programStateMap[col.programId];

    const cells: Array<CellProps> =
      collectionState && collectionState.type !== 'fetched'
        ? [{ type: 'Busy', value: {} } as const]
        : toCells({
            influencerId: model.influencerId,
            col,
            dispatch,
            isCreatorLive,
          });
    return { id: col.programId + '', cells } as unknown as Row<number>;
  });

  return { rows, schema: tableSchema };
};

export const toSmartTableProps = (
  p: ToTablePropsParams,
): SmartTableProps<number> => {
  const { dispatch } = extendDispatch<CollectionsViewActionsMap>(p.dispatch);

  const tableProps = toTableProps(p);
  const isDraggable = p.selectedList === 'all';
  const onChange: DragTableProps<number>['onChange'] = (rows) =>
    dispatch(
      'reorder',
      rows.map((r) => Number(r.id)),
    );
  return isDraggable
    ? { type: 'draggable', value: { ...tableProps, onChange } }
    : { type: 'standard', value: tableProps };
};

type ModalType =
  | Typed<'none'>
  | Typed<'openGuide'>
  | Typed<'openSelectionModal'>
  | Typed<'openPriceModal'>
  | Typed<'requestingExclusiveModal'>
  | TypedValue<'linksModal', LiteCollection>
  | TypedValue<'archiveModal', number>
  | TypedValue<'deleteModal', number>;

type ViewState = {
  openModal: ModalType;
};

export const useCollectionsViewModel = (
  p: UseCollectionsModelParams,
): Async<CollectionsViewModel> => {
  const [viewState, setViewState] = useState<ViewState>({
    openModal: { type: 'none' },
  });
  const { tabHash, setTabHash } = useTabHash(optionTypes, 'all');
  const asyncModel = useCollectionsModel(p);

  if (asyncModel.type !== 'fetched') return asyncModel;
  const model = asyncModel.value;
  const dispatch = getDispatcher(asyncModel, setTabHash, setViewState);
  const createLink = PATHS.NEW_PROGRAM(model.influencerId, {
    salesType: p.salesType,
    from: getCurrentPath(true),
  });
  const isCreatorLive = asyncModel.value.isCreatorLive;

  return {
    type: 'fetched',
    value: {
      listOptions: toListOptions(),
      dispatch,
      selectedList: tabHash,
      mode: toCollectionsMode({
        model,
        dispatch,
        selectedList: tabHash,
        isCreatorLive,
      }),
      influencerId: model.influencerId,
      createLink,
      emptyState: {
        otp: {
          isGuideOpen: viewState.openModal.type === 'openGuide',
          closeGuide: () => dispatch({ type: 'closeGuide' }),
          openGuide: () => dispatch({ type: 'openGuide' }),
          name: model.influencerName,
          guideLink:
            'https://help.playbookapp.io/en/collections/9730963-set-your-offering',
        },
        subscription: {
          name: model.influencerName,
          guideLink:
            'https://help.playbookapp.io/en/articles/9693285-subscription-business-what-a-successful-experience-looks-like',
          options: toSubscriptionEmptyStateOptionsByMode({
            onSelect: (isPPC: boolean) =>
              dispatch({ type: isPPC ? 'selectPPC' : 'selectSub' }),
            disabledType: model.ppc.requested ? 'platform' : undefined,
          }),
        },
      },
      showCreate: model.hasAnyCollection,
      selectionModal: {
        open: viewState.openModal.type === 'openSelectionModal',
        close: () => dispatch({ type: 'closeSelectionModal' }),
      },
      subscription: {
        ppc: {
          requested: model.ppc.requested,
          enabled: model.ppc.enabled,
          showTag:
            p.salesType === 'subscription' &&
            (model.ppc.enabled || model.ppc.requested),
          price: model.ppc.price,
          priceModal: {
            close: () => dispatch({ type: 'closeModal' }),
            open: () => dispatch({ type: 'openPriceModal' }),
            isOpen: viewState.openModal.type === 'openPriceModal',
          },
        },
      },
      requestingExclusive: {
        isOpen: viewState.openModal.type === 'requestingExclusiveModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        onContinue: () => dispatch({ type: 'requestConvertToPpc' }),
      },
      linksModal: {
        isOpen: viewState.openModal.type === 'linksModal',
        onClose: () => dispatch({ type: 'closeModal' }),
        data: getProgramLinks(viewState.openModal),
      },
      archiveModal: {
        isOpen: viewState.openModal.type === 'archiveModal',
        close: () => dispatch({ type: 'closeModal' }),
        archive: () =>
          dispatch({
            type: 'archiveProgram',
            payload:
              viewState.openModal.type === 'archiveModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
      deleteModal: {
        isOpen: viewState.openModal.type === 'deleteModal',
        close: () => dispatch({ type: 'closeModal' }),
        onDelete: () =>
          dispatch({
            type: 'deleteProgram',
            payload:
              viewState.openModal.type === 'deleteModal'
                ? viewState.openModal.value
                : 0,
          }),
      },
    },
  };
};

const getProgramLinks = (p: ViewState['openModal']) => {
  if (p.type !== 'linksModal') return undefined;

  if (p.value.accessType === 'exclusive') {
    return {
      programName: p.value.title,
      links: [
        {
          title: 'Program deep link',
          url: p.value.deeplinkUrl || '',
        },
        ...(p.value.challengeDeeplinkURL
          ? [
              {
                title: 'Challenge deep link',
                url: p.value.challengeDeeplinkURL,
              },
            ]
          : []),
        {
          title: 'Program Web Page',
          url: toProgramLandingPageUrl({
            id: p.value.programId,
            name: p.value.title,
            influencer: {
              customURL: p.value.customURL,
            },
          }),
        },
        {
          title: 'Program Checkout Page',
          url: toProgramCheckoutUrl({
            id: p.value.programId,
            name: p.value.title,
            influencer: {
              customURL: p.value.customURL,
            },
          }),
        },
      ],
    };
  }
  return {
    programName: p.value.title,
    links: [
      {
        title: 'Subscription landing page',
        url: toProgramLandingPageUrl({
          id: p.value.programId,
          name: p.value.title,
          influencer: {
            customURL: p.value.customURL,
          },
        }),
      },
      ...(p.value.challengeDeeplinkURL
        ? [
            {
              title: 'Challenge deep link',
              url: p.value.challengeDeeplinkURL,
            },
          ]
        : []),
      {
        title: 'Subscription checkout page',
        url: toProgramCheckoutUrl({
          id: p.value.programId,
          name: p.value.title,
          influencer: {
            customURL: p.value.customURL,
          },
        }),
      },
      {
        title: 'Playbook app channel',
        url: p.value.profileDeepLink,
      },
      {
        title: 'Collection in-app page',
        url: p.value.deeplinkUrl || '',
      },
      {
        title: 'Collection web page',
        url: getSalesPageUrl({
          customURL: p.value.customURL,
          id: p.value.programId,
          name: p.value.title,
        }),
      },
    ],
  };
};
